/* eslint-disable indent */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Card, CardContent, Button, TextField, Grid, Typography, Box } from '@material-ui/core';
import { OrganizationDropdown, PropertyDropdown, FieldDropdown } from 'components';
import { useProperties, useFields, useOrganizations, useSettings } from 'hooks';
import { DashboardHelpers, RegEx } from 'utils';
import { red } from '@mui/material/colors';

import _ from 'lodash';
import { Checkbox } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  root: { overflow: 'visible' },
  alert: {
    marginBottom: theme.spacing(3),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
    width: '100%',
    flexGrow: 1,
  },
  formGroupGrid: {
    marginBottom: theme.spacing(3),
    flexGrow: 1,
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  fieldHint: {
    margin: theme.spacing(1, 0),
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  tags: {
    marginTop: theme.spacing(1),
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40%',
    [theme.breakpoints.down('md')]: {
      width: '90%',
    },
    bgcolor: 'background.white',
    boxShadow: 24,
  },
  modalActionCancel: {
    marginLeft: '2%',
  },
  modalActionPrimary: {
    marginLeft: '5%',
  },
  helperTextLong: { fontSize: 10, marginBottom: 10, color: red[600] },
  helperText: { fontSize: 12, marginBottom: 10, color: red[600] },
}));

const DeviceEditModalForm = ({ device, submitEdits, hide }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isSuper } = useSettings();

  const [hwAddressError, setHWAddressError] = useState('');
  const [formInvalid, setFormInvalid] = useState(true);
  const { properties, selectedProperty, fetchProperties } = useProperties();
  const { fields, selectedField, fetchFields } = useFields();
  const { organizations, selectedOrganization } = useOrganizations();

  const editDevice = !!device?.hw_address;
  const orgFind = _.find(organizations, { id: device.organization_id });
  const [localSelectedOrganization, setLocalSelectedOrganization] = useState(
    editDevice && orgFind
      ? orgFind
      : editDevice && selectedOrganization
      ? selectedOrganization
      : { id: DashboardHelpers.StatusCodes.NoOrganizationSelected, name: t('none_selected') }
  );

  const [localSelectedProperty, setLocalSelectedProperty] = useState(
    editDevice
      ? device.property_id !== DashboardHelpers.StatusCodes.NoPropertySelected
        ? _.find(properties, { id: device.property_id })
        : !!selectedProperty && selectedProperty !== DashboardHelpers.StatusCodes.NoPropertySelected
        ? selectedProperty
        : { id: DashboardHelpers.StatusCodes.NoPropertySelected, name: t('none_selected') }
      : { id: DashboardHelpers.StatusCodes.NoPropertySelected, name: t('none_selected') }
  );

  const [localSelectedField, setLocalSelectedField] = useState(
    editDevice
      ? device.field_id !== DashboardHelpers.StatusCodes.NoFieldSelected
        ? _.find(fields, { id: device.field_id })
        : !!selectedField && selectedField !== DashboardHelpers.StatusCodes.NoFieldSelected
        ? selectedField
        : { id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') }
      : { id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') }
  );

  const fieldUnselectedWithProp =
    localSelectedProperty?.id !== DashboardHelpers.StatusCodes.NoPropertySelected &&
    localSelectedField?.id === DashboardHelpers.StatusCodes.NoFieldSelected;

  const selectOrganization = (organization) => {
    if (organization.id !== localSelectedOrganization.id) {
      setLocalSelectedOrganization(organization);
      fetchProperties(organization.id);
      fetchFields(undefined, organization.id);
      setLocalSelectedProperty({ id: DashboardHelpers.StatusCodes.NoPropertySelected, name: t('none_selected') }); // Reset property if organization is changed
      setLocalSelectedField({ id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') }); // Reset field if property is changed
    }
  };

  const selectProperty = (property) => {
    if (
      (!localSelectedOrganization ||
        localSelectedOrganization.id === DashboardHelpers.StatusCodes.NoOrganizationSelected) &&
      !selectedOrganization
    ) {
      const localSelectedOrganization = _.find(organizations, { id: property.organization_id });
      setLocalSelectedOrganization(localSelectedOrganization);
    }
    if (property.id !== localSelectedProperty.id) {
      setLocalSelectedField({ id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') }); // Reset field if property is changed
      setLocalSelectedProperty(property);
    }
  };

  const selectField = (field) => {
    if (field.id !== DashboardHelpers.StatusCodes.NoFieldSelected) {
      if (localSelectedProperty.id === DashboardHelpers.StatusCodes.NoPropertySelected) {
        const thisSelectedProperty = _.find(properties, { id: field.property_id });
        selectProperty(thisSelectedProperty);
      }
      setLocalSelectedField(field);
    } else {
      setLocalSelectedField({ id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') });
    }
  };

  const [newDeviceValues, setNewDeviceValues] = useState(
    editDevice
      ? {
          name: device.name === undefined ? '' : device.name,
          organization_id: device.organization_id === undefined ? localSelectedOrganization.id : device.organization_id,
          field: {
            field_id: device.field_id === undefined ? DashboardHelpers.StatusCodes.NoFieldSelected : device.field_id,
            name: device.field_name === undefined ? 'No Field' : device.field_name,
          },
          hw_address: device.hw_address === undefined ? '' : device.hw_address,
          id: device.id === undefined ? undefined : device.id,
          is_inactive: device.is_inactive === undefined ? false : device.is_inactive,
          soil_texture: device?.soil_texture ? device.soil_texture : soil_texture,
          soil_density: device?.soil_density ? device.soil_density : '',
        }
      : {
          name: '',
          organization_id: '',
          field: { field_id: '', name: '' },
          hw_address: '',
          id: undefined,
          is_inactive: false,
          soil_texture: { sand: Number(''), silt: Number(''), clay: Number('') },
          soil_density: '',
        }
  );

  const handleFieldChange = (event, field, value) => {
    if (field == 'sand' || field == 'silt' || field == 'clay') {
      if (Number(value) <= 100 && texturesAreEmpty(field, value)) {
        setSoilTexture({ sand: Number(''), silt: Number(''), clay: Number('') });
        setNewDeviceValues({
          ...newDeviceValues,
          soil_texture: { sand: Number(''), silt: Number(''), clay: Number('') },
        });
      } else if (Number(value) <= 100 && texturesAreValid(field, value)) {
        let newTexture = soil_texture;
        newTexture[field] = Number(value);
        setSoilTexture(newTexture);
        setNewDeviceValues({ ...newDeviceValues, soil_texture: newTexture });
      }
    } else if (field == 'soil_density') {
      setSoilDensity(value);
      setNewDeviceValues({ ...newDeviceValues, soil_density: value });
    } else setNewDeviceValues({ ...newDeviceValues, [field]: value });
  };

  const handleSubmit = () => {
    const wellFormedValues = {
      hw_address: newDeviceValues.hw_address,
      name: newDeviceValues.name,
      organization_id: localSelectedOrganization.id ? localSelectedOrganization.id : selectedOrganization.id,
      field_id:
        localSelectedField?.id !== DashboardHelpers.StatusCodes.NoFieldSelected ? localSelectedField.id : undefined,
      device_id: [device.id],
      id: newDeviceValues.id,
      is_inactive: newDeviceValues.is_inactive,
      soil_texture: newDeviceValues.soil_texture,
      soil_density: Number(newDeviceValues.soil_density),
    };

    submitEdits(wellFormedValues);
    hide();
  };

  const texturesAreValid = (name, value) => {
    let newTexture = soil_texture;
    newTexture[name] = Number(value);
    if (Number(newTexture['clay']) + Number(newTexture['sand']) + Number(newTexture['silt']) <= 100) {
      setShowTextureError(false);
      return true;
    } else {
      setShowTextureError(true);
      return false;
    }
  };

  const texturesAreEmpty = (name, value) => {
    let newTexture = soil_texture;
    newTexture[name] = Number(value);
    let textureTotal = Number(newTexture['clay']) + Number(newTexture['sand']) + Number(newTexture['silt']);
    if (textureTotal == 0) {
      setShowTextureError(false);
      return true;
    } else {
      return false;
    }
  };

  const lostFocus = (event) => {
    const realTimeValue = event.target.value;
    if (realTimeValue === '') {
      setHWAddressError(t('required'));
    } else if (!RegEx.isHWAddress(realTimeValue)) {
      setHWAddressError(t('invalid_mac'));
    } else {
      setFormInvalid(false);
      setHWAddressError('');
    }
  };

  const formIsValid = () => {
    return (
      !!device.hw_address ||
      (localSelectedOrganization?.id !== DashboardHelpers.StatusCodes.NoOrganizationSelected && !formInvalid)
    );
  };

  const [soil_texture, setSoilTexture] = useState({ sand: Number(''), silt: Number(''), clay: Number('') });
  const [soil_density, setSoilDensity] = useState();
  const [showTextureError, setShowTextureError] = useState(false);

  useEffect(() => {
    if (device?.soil_density) {
      setSoilDensity(device.soil_density);
      setNewDeviceValues({ ...newDeviceValues, soil_density: device.soil_density });
    } else {
      setSoilDensity(0);
      setNewDeviceValues({ ...newDeviceValues, soil_density: 0 });
    }
    if (device?.soil_texture) {
      setSoilTexture(device.soil_texture);
      setNewDeviceValues({ ...newDeviceValues, soil_texture: device.soil_texture });
    } else {
      setSoilTexture({ sand: Number(''), silt: Number(''), clay: Number('') });
      setNewDeviceValues({
        ...newDeviceValues,
        soil_texture: { sand: Number(''), silt: Number(''), clay: Number('') },
      });
    }
  }, []);

  return (
    <Card className={classes.modal}>
      <CardContent>
        <form
          onSubmit={() => {
            handleSubmit();
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={12}>
              <Typography variant="h3">{editDevice ? t('device_modify') : t('device_create')}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.formGroupGrid}>
                <TextField
                  label={t('device_name')}
                  name="name"
                  autoComplete="name"
                  onChange={(event) => handleFieldChange(event, 'name', event.target.value)}
                  value={newDeviceValues.name}
                  placeholder={t('device_name')}
                  inputProps={{
                    maxLength: 24,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.formGroupGrid}>
                <TextField
                  onBlur={lostFocus}
                  label={t('hw_address')}
                  name="hw_address"
                  disabled={editDevice}
                  onChange={(event) => handleFieldChange(event, 'hw_address', event.target.value)}
                  value={newDeviceValues.hw_address}
                  placeholder={t('hw_address')}
                  helperText={hwAddressError}
                  error={hwAddressError !== ''}
                  required
                />
              </Box>
            </Grid>
            {isSuper && (
              <Grid item xs={12} sm={6}>
                <OrganizationDropdown
                  setSelectedOrganization={selectOrganization}
                  selectedOrganization={localSelectedOrganization}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <PropertyDropdown
                selectedProperty={localSelectedProperty}
                setSelectedProperty={selectProperty}
                properties={properties}
                selectedOrganization={localSelectedOrganization}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldDropdown
                setSelectedField={selectField}
                selectedField={localSelectedField}
                selectedProperty={localSelectedProperty}
                selectedOrganization={localSelectedOrganization}
              />
              <Grid item xs={12} sm={6}>
                {fieldUnselectedWithProp && (
                  <Typography align="center" className={classes.helperTextLong}>
                    {t('field_select_required_with_property')}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Grid
              item
              container
              xs={12}
              sm={12}
              direction="column"
              justifyContent="center"
              alignContent="flex-end"
              alignItems="center"
            >
              <Grid item xs={3} sm={6}>
                <Checkbox
                  title={t('is_inactive')}
                  checked={newDeviceValues.is_inactive}
                  onChange={(event) => handleFieldChange(event, 'is_inactive', event.target.checked)}
                />
              </Grid>
              <Grid item xs={3} sm={6} style={{ paddingTop: 6 }}>
                <Typography>{t('is_inactive')}</Typography>
              </Grid>
            </Grid>

            <Grid item container xs={12}>
              <div>
                <TextField
                  fullWidth
                  label={t('soil_density')}
                  name="soil_density"
                  size="small"
                  autoComplete="soil_density"
                  onChange={(event) => handleFieldChange(event, 'soil_density', event.target.value)}
                  value={soil_density}
                  placeholder={t('soil_density')}
                  inputProps={{
                    maxLength: 24,
                  }}
                  required
                />
              </div>
            </Grid>

            <Grid item container xs={12}>
              <div>
                <Typography variant="subtitle2" gutterBottom component="div">
                  {t('soil_texture')}
                </Typography>
              </div>

              <Grid item container xs={12}>
                <Typography style={{ color: 'red' }} variant="subtitle2" gutterBottom component="div">
                  This texture will override that of parent field for this device.
                </Typography>
                {showTextureError && (
                  <Typography style={{ color: 'red' }} variant="subtitle2" gutterBottom component="div">
                    Clay, sand, and silt must total 100 or less.
                  </Typography>
                )}
              </Grid>

              <Grid item container xs={12}>
                <TextField
                  fullWidth
                  label={t('sand')}
                  name="sand"
                  size="small"
                  autoComplete="sand"
                  onChange={(event) => handleFieldChange(event, 'sand', event.target.value)}
                  value={soil_texture['sand']}
                  placeholder={t('sand')}
                  inputProps={{
                    maxLength: 24,
                  }}
                  required
                />
              </Grid>
              <Grid item container xs={12}>
                <TextField
                  fullWidth
                  label={t('silt')}
                  name="silt"
                  size="small"
                  autoComplete="silt"
                  onChange={(event) => handleFieldChange(event, 'silt', event.target.value)}
                  value={soil_texture['silt']}
                  placeholder={t('silt')}
                  inputProps={{
                    maxLength: 24,
                  }}
                  required
                />
              </Grid>
              <Grid item container xs={12}>
                <TextField
                  fullWidth
                  label={t('clay')}
                  name="clay"
                  size="small"
                  autoComplete="clay"
                  onChange={(event) => handleFieldChange(event, 'clay', event.target.value)}
                  value={soil_texture['clay']}
                  placeholder={t('clay')}
                  inputProps={{
                    maxLength: 24,
                  }}
                  required
                />
              </Grid>
            </Grid>
          </Grid>
          <Button onClick={hide} color="primary" className={classes.modalActionCancel}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!formIsValid() || showTextureError}
            variant="contained"
            className={classes.modalActionPrimary}
          >
            {t('submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

DeviceEditModalForm.propTypes = {
  device: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  submitEdits: PropTypes.func.isRequired,
  hide: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
};

DeviceEditModalForm.defaultProps = {
  device: {},
  fields: {},
};

export default DeviceEditModalForm;
